import React, { useState, useEffect } from "react";
import {
  AlertTriangle,
  CalendarDays,
  Check,
  Copy,
  Eye,
  EyeOff,
  FolderKanban,
  Globe,
  Key,
  Lock,
  Mail,
  Plus,
  Trash2,
  UserCheck,
  Users,
  FileText,
  Settings,
  ArrowRight,
  Image as ImageIcon,
  Zap,
  X,
  Receipt,
  ShieldCheck,
  RefreshCw,
  Phone,
  Pencil,
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import DeleteConfirmModal from "../../components/DeleteConfirmModal";
import CreateEntityModal from "../../components/CreateEntityModal";
import {
  getProject, deleteProject, listApiKeys, createApiKey, revokeApiKey, listManagers, assignManager, removeManager,
  getPublicCredential, regeneratePublicCredential, addPublicDomain, removePublicDomain,
} from "../../services/projectService";
import { getBillingSettings, getInvoiceStats, createInvoice, getUnbilledSummary } from "../../services/invoiceService";
import { listPhoneMappings, createPhoneMapping, updatePhoneMapping, deletePhoneMapping } from "../../services/telephonyService";
import { useAuthedImage } from "../../hooks/useAuthedImage";
import { formatPid } from "../../utils/formatPid";

/* ─────────────────────────────────────────────────────────────── */

const ROLE_META = {
  admin:   { bg: "var(--primaryTint)", color: "var(--primaryColor)", label: "Admin" },
  manager: { bg: "#f0fdf4", color: "#166534", label: "Manager" },
};

const AVATAR_PALETTE = [
  { bg: "var(--primaryTint)", color: "var(--primaryColor)" },
  { bg: "#f0fdf4", color: "#166534" },
  { bg: "#fff7ed", color: "#c2410c" },
  { bg: "#fdf4ff", color: "#7e22ce" },
];

const av = (first, last) =>
  `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase();

/* ── Compact stat card ───────────────────────────────────────── */
const StatCard = ({ icon: Icon, iconBg, iconColor, label, value, sub }) => (
  <div style={{
    background: "#fff", border: "1px solid var(--borderColor)", borderRadius: 12,
    padding: "18px 20px", display: "flex", alignItems: "center", gap: 16,
    boxShadow: "0 1px 3px rgba(15,23,42,.05), 0 4px 12px rgba(15,23,42,.04)", width: "100%",
  }}>
    <div style={{
      width: 48, height: 48, borderRadius: 12, background: iconBg, color: iconColor,
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>
      <Icon size={22} />
    </div>
    <div style={{ minWidth: 0, flex: 1 }}>
      <p style={{ margin: 0, fontSize: 12, color: "var(--muteColor)", fontWeight: 500, marginBottom: 2 }}>{label}</p>
      <h5 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#0f172a", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {value}
      </h5>
      {sub && <p style={{ margin: "4px 0 0", fontSize: 11, color: "var(--muteColor)" }}>{sub}</p>}
    </div>
  </div>
);

/* ════════════════════════════════════════════════════════════ */
const ProjectDetails = () => {
  const { id: projectPid } = useParams();
  const navigate = useNavigate();
  const role = localStorage.getItem("role");
  const isSuperAdmin = role === "super_admin";

  const [project, setProject]               = useState(null);
  const [managers, setManagers]             = useState([]);
  const [loadingProject, setLoadingProject] = useState(true);
  const [projectError, setProjectError]     = useState("");

  const [apiKeys, setApiKeys]                   = useState([]);
  const [keysLoading, setKeysLoading]           = useState(true);
  const [showCreateKeyModal, setShowCreateKeyModal] = useState(false);
  const [newKeyName, setNewKeyName]             = useState("");
  const [newKeyExpiry, setNewKeyExpiry]         = useState("");
  const [newKeyCreating, setNewKeyCreating]     = useState(false);
  const [revealedRawKey, setRevealedRawKey]     = useState(null);
  const [copiedKeyPid, setCopiedKeyPid]         = useState(null);
  const [revokingKeyPid, setRevokingKeyPid]     = useState(null);

  const [showApiKey, setShowApiKey] = useState(false);
  const [copied, setCopied]         = useState(false);

  const primaryApiKey = apiKeys.find((k) => k.isActive) ?? apiKeys[0] ?? null;

  const handleCopy = () => {
    if (!primaryApiKey?.keyPrefix) return;
    navigator.clipboard.writeText(primaryApiKey.keyPrefix);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };


  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [managerError, setManagerError]       = useState("");
  const [managerLoading, setManagerLoading]   = useState(false);
  const [managerSearch, setManagerSearch]     = useState("");

  // ── Widget public credential + domain whitelist ──
  const [publicCredential, setPublicCredential] = useState(null);
  const [widgetLoading, setWidgetLoading]       = useState(true);
  const [widgetError, setWidgetError]           = useState("");
  const [newDomain, setNewDomain]               = useState("");
  const [domainError, setDomainError]           = useState("");
  const [addingDomain, setAddingDomain]         = useState(false);
  const [removingDomainId, setRemovingDomainId] = useState(null);
  const [removeDomainTarget, setRemoveDomainTarget] = useState(null); // { id, domain }
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [regenerating, setRegenerating]         = useState(false);
  const [regenerateError, setRegenerateError]   = useState("");
  const [revealedWidgetSecret, setRevealedWidgetSecret] = useState(null);
  const [copiedWidgetSecret, setCopiedWidgetSecret]     = useState(false);

  const fetchPublicCredential = () => {
    if (!projectPid || !isSuperAdmin) return;
    setWidgetLoading(true);
    setWidgetError("");
    getPublicCredential(projectPid)
      .then((res) => setPublicCredential(res.data?.response ?? null))
      .catch(() => setWidgetError("Failed to load widget credential."))
      .finally(() => setWidgetLoading(false));
  };

  useEffect(() => {
    fetchPublicCredential();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectPid]);

  const handleAddDomain = async () => {
    const domain = newDomain.trim();
    if (!domain) return;
    setDomainError("");
    setAddingDomain(true);
    try {
      await addPublicDomain(projectPid, domain);
      setNewDomain("");
      fetchPublicCredential();
    } catch (err) {
      setDomainError(err?.response?.data?.message || "Failed to add domain.");
    } finally {
      setAddingDomain(false);
    }
  };

  const confirmRemoveDomain = async () => {
    if (!removeDomainTarget) return;
    setRemovingDomainId(removeDomainTarget.id);
    try {
      await removePublicDomain(projectPid, removeDomainTarget.id);
      setPublicCredential((prev) => prev ? { ...prev, domains: prev.domains.filter((d) => d.id !== removeDomainTarget.id) } : prev);
    } catch {
      // removal failed — list unchanged
    } finally {
      setRemovingDomainId(null);
      setRemoveDomainTarget(null);
    }
  };

  const handleRegenerateSecret = async () => {
    setRegenerating(true);
    setRegenerateError("");
    try {
      const res = await regeneratePublicCredential(projectPid);
      setRevealedWidgetSecret(res.data?.response?.rawSecret ?? null);
      setShowRegenerateConfirm(false);
      fetchPublicCredential();
    } catch (err) {
      setRegenerateError(err?.response?.data?.message || "Failed to regenerate secret.");
    } finally {
      setRegenerating(false);
    }
  };

  const handleCopyWidgetSecret = () => {
    if (!revealedWidgetSecret) return;
    navigator.clipboard.writeText(revealedWidgetSecret);
    setCopiedWidgetSecret(true);
    setTimeout(() => setCopiedWidgetSecret(false), 1500);
  };

  // ── Phone number → project mapping (telephony) ──
  const [phoneMappings, setPhoneMappings]       = useState([]);
  const [phoneLoading, setPhoneLoading]         = useState(true);
  const [phoneError, setPhoneError]             = useState("");
  const [newPhoneNumber, setNewPhoneNumber]     = useState("");
  const [phoneFormError, setPhoneFormError]     = useState("");
  const [savingPhone, setSavingPhone]           = useState(false);
  const [editingMapping, setEditingMapping]     = useState(null); // { mappingPid, phoneNumber } | null
  const [removeMappingTarget, setRemoveMappingTarget] = useState(null); // { mappingPid, phoneNumber }
  const [removingMappingPid, setRemovingMappingPid]   = useState(null);

  const fetchPhoneMappings = () => {
    if (!projectPid || !isSuperAdmin) return;
    setPhoneLoading(true);
    setPhoneError("");
    listPhoneMappings(projectPid)
      .then((res) => setPhoneMappings(Array.isArray(res.data?.response) ? res.data.response : []))
      .catch(() => setPhoneError("Failed to load phone numbers."))
      .finally(() => setPhoneLoading(false));
  };

  useEffect(() => {
    fetchPhoneMappings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectPid]);

  const openAddPhoneForm = () => {
    setEditingMapping(null);
    setNewPhoneNumber("");
    setPhoneFormError("");
  };

  const openEditPhoneForm = (mapping) => {
    setEditingMapping({ mappingPid: mapping.mapping_pid, phoneNumber: mapping.phone_number });
    setNewPhoneNumber(mapping.phone_number);
    setPhoneFormError("");
  };

  const handleSavePhone = async () => {
    const phoneNumber = newPhoneNumber.trim();
    if (!/^\+\d{8,15}$/.test(phoneNumber)) {
      setPhoneFormError("Enter a valid E.164 number, e.g. +15550123456.");
      return;
    }
    setPhoneFormError("");
    setSavingPhone(true);
    try {
      if (editingMapping) {
        await updatePhoneMapping(editingMapping.mappingPid, { phoneNumber });
      } else {
        await createPhoneMapping(projectPid, phoneNumber);
      }
      setEditingMapping(null);
      setNewPhoneNumber("");
      fetchPhoneMappings();
    } catch (err) {
      setPhoneFormError(err?.response?.data?.message || "Failed to save phone number.");
    } finally {
      setSavingPhone(false);
    }
  };

  const confirmRemoveMapping = async () => {
    if (!removeMappingTarget) return;
    setRemovingMappingPid(removeMappingTarget.mappingPid);
    try {
      await deletePhoneMapping(removeMappingTarget.mappingPid);
      setPhoneMappings((prev) => prev.filter((m) => m.mapping_pid !== removeMappingTarget.mappingPid));
    } catch {
      // removal failed — list unchanged
    } finally {
      setRemovingMappingPid(null);
      setRemoveMappingTarget(null);
    }
  };

  const [billingTemplate, setBillingTemplate] = useState(null);
  const [unbilledSummary, setUnbilledSummary] = useState(null);
  const [unbilledLoading, setUnbilledLoading] = useState(true);
  const [invoiceStats, setInvoiceStats] = useState({
    sentCount: 0, paidCount: 0, totalInvoiced: 0, totalPaid: 0, totalOutstanding: 0, currency: "USD", currencySymbol: "$",
  });
  const invoiceLogoSrc = useAuthedImage(billingTemplate?.theme?.logoUrl);
  const [showGenerateInvoiceModal, setShowGenerateInvoiceModal] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [generateInvoiceError, setGenerateInvoiceError] = useState("");
  const [generateInvoiceDueDate, setGenerateInvoiceDueDate] = useState("");

  useEffect(() => {
    if (!projectPid) return;
    getBillingSettings(projectPid)
      .then((res) => setBillingTemplate(res.data?.response ?? null))
      .catch(() => setBillingTemplate(null));
    getInvoiceStats(projectPid)
      .then((res) => { if (res.data?.response) setInvoiceStats(res.data.response); })
      .catch(() => {});
    setUnbilledLoading(true);
    getUnbilledSummary(projectPid)
      .then((res) => setUnbilledSummary(res.data?.response ?? null))
      .catch(() => setUnbilledSummary(null))
      .finally(() => setUnbilledLoading(false));
  }, [projectPid]);

  const handleGenerateInvoice = async () => {
    setGeneratingInvoice(true);
    setGenerateInvoiceError("");
    try {
      const payload = {
        projectPid,
        issueDate: new Date().toISOString().slice(0, 10),
      };
      if (generateInvoiceDueDate) payload.dueDate = generateInvoiceDueDate;
      const res = await createInvoice(payload);
      setShowGenerateInvoiceModal(false);
      const invoicePid = res.data?.response?.invoicePid;
      navigate(invoicePid
        ? `/project-details/${projectPid}/invoices/${invoicePid}/view`
        : `/project-details/${projectPid}/invoices`);
    } catch (err) {
      setGenerateInvoiceError(err?.response?.data?.message || "Failed to generate invoice.");
    } finally {
      setGeneratingInvoice(false);
    }
  };

  useEffect(() => {
    if (!projectPid) return;
    setLoadingProject(true);
    setProjectError("");
    getProject(projectPid)
      .then((res) => {
        const data = res.data?.response ?? res.data;
        setProject(data);
      })
      .catch(() => setProjectError("Failed to load project."))
      .finally(() => setLoadingProject(false));
  }, [projectPid]);

  useEffect(() => {
    if (!projectPid) return;
    listManagers(projectPid)
      .then((res) => setManagers(res.data?.response ?? res.data ?? []))
      .catch(() => setManagers([]));
  }, [projectPid]);

  const openAddManagerModal = () => {
    setShowCreateModal(true);
  };

  const handleAssignManager = async (formData) => {
    setManagerError("");
    setManagerLoading(true);
    try {
      if (formData._mode === "create") {
        await assignManager(projectPid, { firstName: formData.firstName, lastName: formData.lastName, email: formData.email, role: formData.role });
      } else {
        await assignManager(projectPid, { userPid: formData.userPid });
      }
      const res = await listManagers(projectPid);
      setManagers(res.data?.response ?? res.data ?? []);
      setShowCreateModal(false);
    } catch (err) {
      const msg = err?.response?.data?.message ?? err?.response?.data?.error ?? "Failed to add manager. Please try again.";
      setManagerError(msg);
    } finally {
      setManagerLoading(false);
    }
  };

  const handleRemoveManager = async () => {
    if (!selectedManager) return;
    try {
      await removeManager(projectPid, selectedManager.user_pid);
      setManagers((prev) => prev.filter((m) => m.user_pid !== selectedManager.user_pid));
    } catch {
      // removal failed — list unchanged
    } finally {
      setShowDeleteModal(false);
      setSelectedManager(null);
    }
  };

  const normalizeApiKey = (k) => ({
    keyPid: k.keyPid ?? k.key_pid,
    keyPrefix: k.keyPrefix ?? k.key_prefix,
    keyName: k.keyName ?? k.key_name,
    lastUsedAt: k.lastUsedAt ?? k.last_used_at,
    expiresAt: k.expiresAt ?? k.expires_at,
    isActive: (k.isActive ?? k.is_active) === 1 || (k.isActive ?? k.is_active) === true,
    createdAt: k.createdAt ?? k.created_at,
  });

  useEffect(() => {
    if (!projectPid) return;
    setKeysLoading(true);
    listApiKeys(projectPid)
      .then((res) => setApiKeys((res.data?.response ?? res.data ?? []).map(normalizeApiKey)))
      .catch(() => setApiKeys([]))
      .finally(() => setKeysLoading(false));
  }, [projectPid]);

  const handleCreateKey = async (e) => {
    e.preventDefault();
    setNewKeyCreating(true);
    try {
      const res = await createApiKey(projectPid, newKeyName, newKeyExpiry || null);
      const created = res.data?.response ?? res.data;
      setRevealedRawKey(created?.rawKey ?? null);
      setApiKeys((prev) => [
        ...prev,
        normalizeApiKey({
          keyPid: created?.keyPid,
          keyPrefix: created?.keyPrefix,
          keyName: newKeyName,
          expiresAt: newKeyExpiry || null,
          isActive: true,
        }),
      ]);
      setShowCreateKeyModal(false);
      setNewKeyName("");
      setNewKeyExpiry("");
    } catch {
      // leave modal open so user can retry
    } finally {
      setNewKeyCreating(false);
    }
  };

  const handleRevokeKey = async (keyPid) => {
    setRevokingKeyPid(keyPid);
    try {
      await revokeApiKey(projectPid, keyPid);
      setApiKeys((prev) => prev.map((k) => k.keyPid === keyPid ? { ...k, isActive: false } : k));
    } catch {
      // silently keep key active if request fails
    } finally {
      setRevokingKeyPid(null);
    }
  };

  const handleCopyKey = (text, pid) => {
    navigator.clipboard.writeText(text);
    setCopiedKeyPid(pid);
    setTimeout(() => setCopiedKeyPid(null), 2000);
  };

  if (loadingProject) {
    return <main className="app-wrapper url-list-page"><div className="container-fluid py-5 text-center text-muted">Loading...</div></main>;
  }
  if (projectError) {
    return <main className="app-wrapper url-list-page"><div className="container-fluid py-5 text-center text-danger">{projectError}</div></main>;
  }

  const isActive = project?.is_active === 1;
  const createdDate = project?.created_at ? new Date(project.created_at) : null;

  const _q = managerSearch.trim().toLowerCase();
  const filteredManagers = _q
    ? managers.filter((m) =>
        (m.first_name ?? "").toLowerCase().includes(_q) ||
        (m.last_name  ?? "").toLowerCase().includes(_q) ||
        (m.email      ?? "").toLowerCase().includes(_q) ||
        (m.user_pid   ?? "").toLowerCase().includes(_q)
      )
    : managers;

  return (
    <main className="app-wrapper url-list-page">
      <div className="container-fluid">

        {/* ── Suspended banner ── */}
        {!isActive && (
          <div style={{
            display: "flex", alignItems: "center", gap: 12,
            background: "#fff1f2", border: "1px solid #fecdd3", borderRadius: 12,
            padding: "14px 20px", marginBottom: 16,
          }}>
            <div style={{ width: 36, height: 36, borderRadius: 9, background: "#fee2e2", color: "#dc2626", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <AlertTriangle size={18} />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#991b1b" }}>This project is suspended</p>
              <p style={{ margin: 0, fontSize: 12, color: "#b91c1c" }}>API access is disabled. Reveal, copy, and management actions are locked until the project is reactivated.</p>
            </div>
          </div>
        )}

        {/* ── Stat cards ── */}
        <div className="row g-3 mb-4">
          <div className="col-12 col-sm-6 col-xl">
            <StatCard icon={FolderKanban} iconBg="var(--primaryTint)" iconColor="var(--primaryColor)"
              label="Project Key" value={formatPid(project?.project_pid)} sub="Unique identifier" />
          </div>
          <div className="col-12 col-sm-6 col-xl">
            <StatCard icon={Users} iconBg="#fdf4ff" iconColor="#7e22ce"
              label="Managers" value={project?.manager_count ?? managers.length}
              sub={`${project?.manager_count ?? managers.length} assigned`} />
          </div>
          <div className="col-12 col-sm-6 col-xl">
            <StatCard icon={Globe} iconBg="#f0f9ff" iconColor="#0369a1"
              label="Domain" value={project?.domain ?? "—"} sub="Project domain" />
          </div>
          <div className="col-12 col-sm-6 col-xl">
            <StatCard icon={CalendarDays} iconBg="#fff7ed" iconColor="var(--orangeColor)"
              label="Created"
              value={createdDate ? createdDate.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }) : "—"}
              sub={createdDate ? createdDate.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", timeZoneName: "short" }) : ""} />
          </div>
        </div>

        {/* ── Invoices & Billing Settings ── */}
        <div className="row g-3 mb-4">
          <div className="col-12">
            <div style={{ background: "#fff", border: "1px solid var(--borderColor)", borderRadius: 14, boxShadow: "0 1px 3px rgba(15,23,42,.05), 0 4px 12px rgba(15,23,42,.03)", overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: "1px solid var(--borderColor)", background: "#fafbfc", flexWrap: "wrap", gap: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 9, background: "var(--primaryTint)", color: "var(--primaryColor)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <FileText size={16} />
                  </div>
                  <div>
                    <h6 style={{ margin: 0, fontWeight: 700, fontSize: 13, color: "#0f172a" }}>Invoices &amp; Billing</h6>
                    <p style={{ margin: 0, fontSize: 11, color: "var(--muteColor)" }}>Manage the billing template and view invoices sent to this project's client</p>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    className="btn btn-outline-success btn-sm d-flex align-items-center gap-2"
                    style={{ borderRadius: 8 }}
                    onClick={() => { setGenerateInvoiceError(""); setGenerateInvoiceDueDate(""); setShowGenerateInvoiceModal(true); }}
                  >
                    <Zap size={13} /> Generate Invoice
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
                    style={{ borderRadius: 8 }}
                    onClick={() => navigate(`/project-details/${projectPid}/invoices/settings`)}
                  >
                    <Settings size={13} /> Billing Settings
                  </button>
                  <button
                    className="btn btn-primary btn-sm d-flex align-items-center gap-2"
                    style={{ borderRadius: 8 }}
                    onClick={() => navigate(`/project-details/${projectPid}/invoices`)}
                  >
                    <FileText size={13} /> View Invoices <ArrowRight size={13} />
                  </button>
                </div>
              </div>

              <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
                <div style={{ width: 44, height: 44, borderRadius: 10, background: "#f8fafc", border: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
                  {invoiceLogoSrc
                    ? <img src={invoiceLogoSrc} alt="logo" style={{ maxWidth: "100%", maxHeight: "100%" }} />
                    : <ImageIcon size={18} color="#94a3b8" />}
                </div>
                <div style={{ minWidth: 140 }}>
                  <p style={{ margin: 0, fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>Business Name</p>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{billingTemplate?.from?.name || "Not set"}</p>
                </div>
                <div style={{ minWidth: 100 }}>
                  <p style={{ margin: 0, fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>Currency</p>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{invoiceStats.currency || "USD"}</p>
                </div>
                <div style={{ minWidth: 100 }}>
                  <p style={{ margin: 0, fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>Invoices Sent</p>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{invoiceStats.sentCount}</p>
                </div>
                <div style={{ minWidth: 120 }}>
                  <p style={{ margin: 0, fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>Total Invoiced</p>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#166534" }}>
                    {invoiceStats.currencySymbol || "$"}{Number(invoiceStats.totalInvoiced || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div style={{ minWidth: 120 }}>
                  <p style={{ margin: 0, fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>Total Paid</p>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                    {invoiceStats.currencySymbol || "$"}{Number(invoiceStats.totalPaid || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                {!billingTemplate && (
                  <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: "#fff7ed", color: "var(--orangeColor)", marginLeft: "auto" }}>
                    Billing template not configured
                  </span>
                )}
              </div>
              <div style={{ padding: "0 20px 14px" }}>
                <p style={{ margin: 0, fontSize: 11, color: "#94a3b8" }}>These stats don't include draft invoices that haven't been sent yet.</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── API Key + Billing side by side ── */}
        <div className="row g-3 align-items-stretch">

          {/* API Key col */}
          <div className="col-12 col-lg-5" style={{ display: "flex" }}>
            <div style={{ background: "#fff", border: "1px solid var(--borderColor)", borderRadius: 14, boxShadow: "0 1px 3px rgba(15,23,42,.05), 0 4px 12px rgba(15,23,42,.03)", overflow: "hidden", width: "100%" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 20px", borderBottom: "1px solid var(--borderColor)", background: "#fafbfc" }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: "#fff7ed", color: "var(--orangeColor)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Key size={16} />
                </div>
                <div>
                  <h6 style={{ margin: 0, fontWeight: 700, fontSize: 13, color: "#0f172a" }}>API Key</h6>
                  <p style={{ margin: 0, fontSize: 11, color: "var(--muteColor)" }}>Never expose this in client-side or public code</p>
                </div>
              </div>
              <div style={{ padding: "18px 20px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#f8fafc", border: "1px solid #e8edf3", borderRadius: 10, padding: "12px 14px", marginBottom: 10, opacity: isActive ? 1 : 0.55 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: isActive ? "#fff7ed" : "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {isActive ? <Key size={13} color="var(--orangeColor)" /> : <Lock size={13} color="#94a3b8" />}
                  </div>
                  <code style={{ flex: 1, fontSize: 12, color: "#334155", letterSpacing: (isActive && showApiKey) ? 0.3 : 5, fontFamily: "monospace", userSelect: "none", wordBreak: "break-all", minWidth: 0 }}>
                    {isActive && showApiKey
                      ? (primaryApiKey?.keyPrefix || "No active key")
                      : "••••••••••••••••••••••••••••"}
                  </code>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => isActive && setShowApiKey((v) => !v)}
                    disabled={!isActive || !primaryApiKey}
                    className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                    style={{ borderRadius: 7, fontSize: 12, flex: 1, justifyContent: "center" }}
                    title={!isActive ? "Project is suspended" : !primaryApiKey ? "No API key created yet" : undefined}
                  >
                    {isActive && showApiKey ? <EyeOff size={13} /> : <Eye size={13} />}
                    {isActive && showApiKey ? "Hide" : "Reveal"}
                  </button>
                  <button
                    onClick={isActive ? handleCopy : undefined}
                    disabled={!isActive || !primaryApiKey}
                    className={`btn btn-sm d-flex align-items-center gap-1 ${copied ? "btn-success" : "btn-outline-primary"}`}
                    style={{ borderRadius: 7, fontSize: 12, flex: 1, justifyContent: "center" }}
                    title={!isActive ? "Project is suspended" : !primaryApiKey ? "No API key created yet" : undefined}
                  >
                    {copied ? <Check size={13} /> : <Copy size={13} />}
                    {copied ? "Copied!" : "Copy Key"}
                  </button>
                </div>
                <p style={{ margin: "10px 0 16px", fontSize: 11, color: isActive ? "#94a3b8" : "#dc2626", fontWeight: isActive ? 400 : 600 }}>
                  {isActive ? "Use this key to authenticate API requests from your server." : "API access is disabled while this project is suspended."}
                </p>

                <div style={{ borderTop: "1px solid var(--borderColor)", paddingTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                  {[
                    { label: "Key Status",    value: isActive ? "Active" : "Inactive", valueColor: isActive ? "#166534" : "#dc2626", valueBg: isActive ? "#f0fdf4" : "#fff1f2" },
                    { label: "Project",       value: project?.project_name ?? "—",     valueColor: "#334155", valueBg: null },
                    { label: "Created",       value: createdDate ? createdDate.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }) : "—", valueColor: "#334155", valueBg: null },
                  ].map((item) => (
                    <div key={item.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 12, color: "var(--muteColor)", fontWeight: 500 }}>{item.label}</span>
                      {item.valueBg ? (
                        <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: item.valueBg, color: item.valueColor }}>{item.value}</span>
                      ) : (
                        <span style={{ fontSize: 12, fontWeight: 600, color: item.valueColor }}>{item.value}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Unbilled Usage col */}
          <div className="col-12 col-lg-7" style={{ display: "flex" }}>
            <div style={{ background: "#fff", border: "1px solid var(--borderColor)", borderRadius: 14, boxShadow: "0 1px 3px rgba(15,23,42,.05), 0 4px 12px rgba(15,23,42,.03)", overflow: "hidden", width: "100%" }}>

              {/* Header */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: "1px solid var(--borderColor)", background: "#fafbfc" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 9, background: "#fff7ed", color: "var(--orangeColor)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Receipt size={16} />
                  </div>
                  <div>
                    <h6 style={{ margin: 0, fontWeight: 700, fontSize: 13, color: "#0f172a" }}>Unbilled Usage</h6>
                    <p style={{ margin: 0, fontSize: 11, color: "var(--muteColor)" }}>What would appear on the next invoice if generated right now</p>
                  </div>
                </div>
                <button
                  className="btn btn-outline-success btn-sm d-flex align-items-center gap-2"
                  style={{ borderRadius: 8, fontSize: 12, paddingInline: 14 }}
                  onClick={() => { setGenerateInvoiceError(""); setGenerateInvoiceDueDate(""); setShowGenerateInvoiceModal(true); }}
                >
                  <Zap size={13} /> Generate Invoice
                </button>
              </div>

              {unbilledLoading ? (
                <div className="py-5 text-center text-muted" style={{ fontSize: 13 }}>Loading…</div>
              ) : !unbilledSummary ? (
                <div className="py-5 text-center text-muted" style={{ fontSize: 13 }}>Failed to load unbilled usage.</div>
              ) : (
                <>
                  {/* Summary row — billed amounts (multiplier applied), not raw usage cost */}
                  <div style={{ display: "flex", gap: 0, borderBottom: "1px solid var(--borderColor)", flexWrap: "wrap" }}>
                    {[
                      { label: "Text (LLM, Embed)", value: `${unbilledSummary.currencySymbol}${Number(unbilledSummary.items?.find((it) => /llm|embed/i.test(it.description))?.rate ?? 0).toFixed(2)}` },
                      { label: "Voice (TTS, STT)",   value: `${unbilledSummary.currencySymbol}${Number(unbilledSummary.items?.find((it) => /tts|stt/i.test(it.description))?.rate ?? 0).toFixed(2)}` },
                      { label: "Total Unbilled",     value: `${unbilledSummary.currencySymbol}${Number(unbilledSummary.total ?? 0).toFixed(2)}`, highlight: true },
                    ].map((s, i, arr) => (
                      <div key={s.label} style={{ flex: "1 1 120px", padding: "12px 16px", borderRight: i < arr.length - 1 ? "1px solid var(--borderColor)" : "none" }}>
                        <p style={{ margin: "0 0 3px", fontSize: 11, color: "var(--muteColor)", fontWeight: 500 }}>{s.label}</p>
                        <span style={{ fontSize: 15, fontWeight: 800, color: s.highlight ? "#166534" : "#0f172a" }}>{s.value}</span>
                      </div>
                    ))}
                  </div>

                  {/* Line items table */}
                  <div className="table-responsive">
                    <table className="table url-table mb-0">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Description</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(unbilledSummary.items ?? []).length === 0 ? (
                          <tr><td colSpan={3} className="text-center text-muted py-4">No unbilled usage yet.</td></tr>
                        ) : unbilledSummary.items.map((it, i) => (
                          <tr key={i}>
                            <td className="url-index">{i + 1}</td>
                            <td style={{ fontSize: 13 }}>{it.description}</td>
                            <td><span style={{ fontSize: 13, fontWeight: 700, color: "#166534" }}>{unbilledSummary.currencySymbol}{Number(it.rate ?? 0).toFixed(2)}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ padding: "10px 20px" }}>
                    <p style={{ margin: 0, fontSize: 11, color: "#94a3b8" }}>Excludes fixed charges, taxes, and discounts — those are computed when the invoice is actually generated.</p>
                  </div>
                </>
              )}

            </div>
          </div>
        </div>{/* end row */}

        {/* ── Widget Settings + Phone Numbers side by side — both super_admin only ── */}
        {isSuperAdmin && (
        <div className="row g-3 mt-1 align-items-stretch">
          <div className="col-12 col-xl-7" style={{ display: "flex" }}>
            <div style={{ background: "#fff", border: "1px solid var(--borderColor)", borderRadius: 14, boxShadow: "0 1px 3px rgba(15,23,42,.05), 0 4px 12px rgba(15,23,42,.03)", overflow: "hidden", width: "100%" }}>
              {/* Header */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: "1px solid var(--borderColor)", background: "#fafbfc", flexWrap: "wrap", gap: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 9, background: "var(--primaryTint)", color: "var(--primaryColor)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <ShieldCheck size={16} />
                  </div>
                  <div>
                    <h6 style={{ margin: 0, fontWeight: 700, fontSize: 13, color: "#0f172a" }}>Widget Settings</h6>
                    <p style={{ margin: 0, fontSize: 11, color: "var(--muteColor)" }}>Public credential &amp; domain whitelist used by the embedded chat widget</p>
                  </div>
                </div>
                {isSuperAdmin && (
                  <button
                    className="btn btn-outline-danger btn-sm d-flex align-items-center gap-2"
                    style={{ borderRadius: 8 }}
                    onClick={() => { setRegenerateError(""); setShowRegenerateConfirm(true); }}
                    disabled={widgetLoading || !publicCredential}
                  >
                    <RefreshCw size={13} /> Regenerate Secret
                  </button>
                )}
              </div>

              {widgetLoading ? (
                <div className="py-5 text-center text-muted" style={{ fontSize: 13 }}>Loading…</div>
              ) : widgetError ? (
                <div className="py-5 text-center text-danger" style={{ fontSize: 13 }}>{widgetError}</div>
              ) : !publicCredential ? (
                <div className="py-5 text-center text-muted" style={{ fontSize: 13 }}>No widget credential found for this project.</div>
              ) : (
                <>
                  {/* Secret + status row */}
                  <div style={{ display: "flex", alignItems: "center", gap: 24, padding: "16px 20px", borderBottom: "1px solid var(--borderColor)", flexWrap: "wrap" }}>
                    <div style={{ minWidth: 160 }}>
                      <p style={{ margin: 0, fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>Public Secret</p>
                      <code style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>•••• {publicCredential.secretLast4}</code>
                    </div>
                    <div style={{ minWidth: 100 }}>
                      <p style={{ margin: 0, fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>Status</p>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 999,
                        background: publicCredential.isActive ? "#f0fdf4" : "#fff1f2",
                        color: publicCredential.isActive ? "#166534" : "#dc2626",
                      }}>
                        {publicCredential.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <div style={{ minWidth: 140 }}>
                      <p style={{ margin: 0, fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>Last Updated</p>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#334155" }}>
                        {publicCredential.updatedAt ? new Date(publicCredential.updatedAt).toLocaleString() : "—"}
                      </p>
                    </div>
                    {!isSuperAdmin && (
                      <span style={{ fontSize: 11, color: "#94a3b8", marginLeft: "auto" }}>Only super admins can regenerate this secret.</span>
                    )}
                  </div>

                  {/* Domain whitelist */}
                  <div style={{ padding: "16px 20px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
                      <h6 style={{ margin: 0, fontWeight: 700, fontSize: 12.5, color: "#0f172a" }}>Allowed Domains</h6>
                    </div>
                    <p style={{ margin: "0 0 12px", fontSize: 11, color: "#94a3b8" }}>
                      Add each domain and subdomain separately — subdomains aren't included automatically.
                    </p>

                    <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                      <input
                        type="text"
                        placeholder="e.g. app.acme.com"
                        value={newDomain}
                        onChange={(e) => { setNewDomain(e.target.value); setDomainError(""); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddDomain(); } }}
                        style={{ flex: "1 1 220px", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none" }}
                      />
                      <button
                        className="btn btn-primary btn-sm d-flex align-items-center gap-2"
                        style={{ borderRadius: 8 }}
                        onClick={handleAddDomain}
                        disabled={addingDomain || !newDomain.trim()}
                      >
                        <Plus size={13} /> {addingDomain ? "Adding…" : "Add Domain"}
                      </button>
                    </div>
                    {domainError && <p style={{ margin: "0 0 10px", fontSize: 12, color: "#dc2626" }}>{domainError}</p>}

                    {(publicCredential.domains ?? []).length === 0 ? (
                      <p style={{ margin: 0, fontSize: 13, color: "#94a3b8", textAlign: "center", padding: "16px 0" }}>No domains whitelisted yet — the widget won't work anywhere until one is added.</p>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                        {publicCredential.domains.map((d, i) => (
                          <div
                            key={d.id}
                            style={{
                              display: "flex", alignItems: "center", justifyContent: "space-between",
                              padding: "10px 4px",
                              borderBottom: i === publicCredential.domains.length - 1 ? "none" : "1px solid #f1f5f9",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <Globe size={13} color="#94a3b8" />
                              <span style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>{d.domain}</span>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                              <span style={{ fontSize: 11, color: "#94a3b8" }}>
                                Added {d.createdAt ? new Date(d.createdAt).toLocaleDateString() : "—"}
                              </span>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                style={{ width: 30, height: 30, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 7 }}
                                onClick={() => setRemoveDomainTarget({ id: d.id, domain: d.domain })}
                                disabled={removingDomainId === d.id}
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── Phone Numbers ── */}
          <div className="col-12 col-xl-5" style={{ display: "flex" }}>
            <div style={{ background: "#fff", border: "1px solid var(--borderColor)", borderRadius: 14, boxShadow: "0 1px 3px rgba(15,23,42,.05), 0 4px 12px rgba(15,23,42,.03)", overflow: "hidden", width: "100%" }}>
              {/* Header */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 20px", borderBottom: "1px solid var(--borderColor)", background: "#fafbfc" }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: "#f0fdf4", color: "var(--successColor)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Phone size={16} />
                </div>
                <div>
                  <h6 style={{ margin: 0, fontWeight: 700, fontSize: 13, color: "#0f172a" }}>Phone Numbers</h6>
                  <p style={{ margin: 0, fontSize: 11, color: "var(--muteColor)" }}>Numbers routed to this project's telephony line</p>
                </div>
              </div>

              <div style={{ padding: "16px 20px" }}>
                {phoneLoading ? (
                  <div className="py-4 text-center text-muted" style={{ fontSize: 13 }}>Loading…</div>
                ) : phoneError ? (
                  <div className="py-4 text-center text-danger" style={{ fontSize: 13 }}>{phoneError}</div>
                ) : (
                  <>
                    {phoneMappings.length === 0 ? (
                      <p style={{ margin: "0 0 14px", fontSize: 13, color: "#94a3b8", textAlign: "center", padding: "10px 0" }}>No phone number mapped to this project yet.</p>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 0, marginBottom: 14 }}>
                        {phoneMappings.map((m, i) => (
                          <div
                            key={m.mapping_pid}
                            style={{
                              display: "flex", alignItems: "center", justifyContent: "space-between",
                              padding: "10px 4px",
                              borderBottom: i === phoneMappings.length - 1 ? "none" : "1px solid #f1f5f9",
                            }}
                          >
                            <div>
                              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{m.phone_number}</p>
                              <p style={{ margin: 0, fontSize: 11, color: "#94a3b8" }}>
                                Added {m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}
                              </p>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                style={{ width: 30, height: 30, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 7 }}
                                onClick={() => openEditPhoneForm(m)}
                              >
                                <Pencil size={13} />
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                style={{ width: 30, height: 30, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 7 }}
                                onClick={() => setRemoveMappingTarget({ mappingPid: m.mapping_pid, phoneNumber: m.phone_number })}
                                disabled={removingMappingPid === m.mapping_pid}
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div style={{ borderTop: phoneMappings.length ? "1px solid var(--borderColor)" : "none", paddingTop: phoneMappings.length ? 14 : 0 }}>
                      <p style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 700, color: "#334155" }}>
                        {editingMapping ? "Edit Number" : "Add a Number"}
                      </p>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <input
                          type="text"
                          placeholder="+15550123456"
                          value={newPhoneNumber}
                          onChange={(e) => { setNewPhoneNumber(e.target.value); setPhoneFormError(""); }}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSavePhone(); } }}
                          style={{ flex: "1 1 180px", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 12px", fontSize: 13, outline: "none" }}
                        />
                        <button
                          className="btn btn-primary btn-sm d-flex align-items-center gap-2"
                          style={{ borderRadius: 8 }}
                          onClick={handleSavePhone}
                          disabled={savingPhone || !newPhoneNumber.trim()}
                        >
                          {editingMapping ? <Pencil size={13} /> : <Plus size={13} />}
                          {savingPhone ? "Saving…" : editingMapping ? "Save" : "Add"}
                        </button>
                        {editingMapping && (
                          <button className="btn btn-light btn-sm" style={{ borderRadius: 8 }} onClick={openAddPhoneForm} disabled={savingPhone}>
                            Cancel
                          </button>
                        )}
                      </div>
                      {phoneFormError && <p style={{ margin: "8px 0 0", fontSize: 12, color: "#dc2626" }}>{phoneFormError}</p>}
                      <p style={{ margin: "8px 0 0", fontSize: 11, color: "#94a3b8" }}>E.164 format — a plus sign followed by 8–15 digits.</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        )}

        {/* ── Managers (full width) ── */}
        <div className="row g-3 mt-1">
          <div className="col-12">
            <div style={{ background: "#fff", border: "1px solid var(--borderColor)", borderRadius: 14, boxShadow: "0 1px 3px rgba(15,23,42,.05), 0 4px 12px rgba(15,23,42,.03)", overflow: "hidden" }}>
              {/* Header */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: "1px solid var(--borderColor)", background: "#fafbfc" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 9, background: "#f0fdf4", color: "var(--successColor)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <UserCheck size={16} />
                  </div>
                  <div>
                    <h6 style={{ margin: 0, fontWeight: 700, fontSize: 13, color: "#0f172a" }}>Project Managers</h6>
                    <p style={{ margin: 0, fontSize: 11, color: "var(--muteColor)" }}>{managers.length} member{managers.length !== 1 ? "s" : ""} with access</p>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ position: "relative" }}>
                    <input
                      type="text"
                      placeholder="Search by name, email, user PID…"
                      value={managerSearch}
                      onChange={(e) => setManagerSearch(e.target.value)}
                      style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: "7px 12px 7px 32px", fontSize: 12, outline: "none", width: 240, color: "#334155" }}
                    />
                    <svg style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                  </div>
                  <button
                    className="btn btn-primary btn-sm d-flex align-items-center gap-2"
                    style={{ borderRadius: 8, fontSize: 12, paddingInline: 14 }}
                    onClick={openAddManagerModal}
                    disabled={!isActive}
                    title={!isActive ? "Project is suspended" : undefined}
                  >
                    <Plus size={13} /> Add Manager
                  </button>
                </div>
              </div>

              {/* Column headers — hidden when list is empty */}
              {managers.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 0, padding: "9px 20px", background: "#f8fafc", borderBottom: "1px solid var(--borderColor)" }}>
                  <div style={{ flex: "0 0 160px", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>User PID</div>
                  <div style={{ flex: "0 0 190px", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Name</div>
                  <div style={{ flex: 1,            fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Email</div>
                  <div style={{ flex: "0 0 140px", fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Assigned At</div>
                  <div style={{ flex: "0 0 44px",  fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", textAlign: "center" }}>Action</div>
                </div>
              )}

              {managers.length === 0 ? (
                <div style={{ padding: "48px 20px", textAlign: "center" }}>
                  <div style={{ width: 56, height: 56, borderRadius: 14, background: "#f1f5f9", margin: "0 auto 12px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Users size={24} color="#94a3b8" strokeWidth={1.5} />
                  </div>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "#334155" }}>No managers yet</p>
                  <p style={{ margin: "4px 0 16px", fontSize: 12, color: "#94a3b8" }}>Add a manager to give someone access to this project.</p>
                  <button
                    className="btn btn-primary btn-sm d-flex align-items-center gap-2 mx-auto"
                    style={{ borderRadius: 8, fontSize: 12 }}
                    onClick={openAddManagerModal}
                    disabled={!isActive}
                    title={!isActive ? "Project is suspended" : undefined}
                  >
                    <Plus size={13} /> Add First Manager
                  </button>
                </div>
              ) : filteredManagers.length === 0 ? (
                <div style={{ padding: "36px 20px", textAlign: "center" }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#334155" }}>No results for "{managerSearch}"</p>
                  <p style={{ margin: "4px 0 0", fontSize: 12, color: "#94a3b8" }}>Try a different name, email, or user PID.</p>
                </div>
              ) : filteredManagers.map((m, i) => {
                const role   = ROLE_META[m.role] || ROLE_META.manager;
                const isLast = i === filteredManagers.length - 1;
                const fullName = [m.first_name, m.last_name].filter(Boolean).join(" ") || "—";
                const assignedDate = m.assigned_at && !isNaN(new Date(m.assigned_at))
                  ? new Date(m.assigned_at).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })
                  : "—";
                return (
                  <div
                    key={m.id}
                    style={{ display: "flex", alignItems: "center", gap: 0, padding: "13px 20px", borderBottom: isLast ? "none" : "1px solid #f1f5f9", transition: "background .15s" }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#fafbfc"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                  >
                    {/* User PID */}
                    <div style={{ flex: "0 0 160px", minWidth: 0 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>{m.user_pid ?? "—"}</span>
                    </div>
                    {/* Name */}
                    <div style={{ flex: "0 0 190px", minWidth: 0 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>{fullName}</span>
                    </div>
                    {/* Email */}
                    <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 6 }}>
                      <Mail size={11} color="#94a3b8" style={{ flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.email ?? "—"}</span>
                    </div>
                    {/* Assigned At */}
                    <div style={{ flex: "0 0 140px" }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>{assignedDate}</span>
                    </div>
                    {/* Action */}
                    <div style={{ flex: "0 0 44px", display: "flex", justifyContent: "center" }}>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        style={{ width: 32, height: 32, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 8 }}
                        onClick={() => { setSelectedManager(m); setShowDeleteModal(true); }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

      </div>

      {/* Generate Invoice Modal */}
      {showGenerateInvoiceModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(15,23,42,0.4)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => !generatingInvoice && setShowGenerateInvoiceModal(false)}>
          <div style={{ background: "#fff", borderRadius: 14, width: 440, maxWidth: "92vw", boxShadow: "0 8px 32px rgba(15,23,42,0.18)" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid var(--borderColor)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: "#f0fdf4", color: "#166534", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Zap size={15} />
                </div>
                <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Generate Invoice</h6>
              </div>
              <button style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b", padding: 4 }} onClick={() => !generatingInvoice && setShowGenerateInvoiceModal(false)}><X size={18} /></button>
            </div>

            <div style={{ padding: 20 }}>
              <p style={{ margin: 0, fontSize: 13, color: "#334155", lineHeight: 1.6 }}>
                This will create a new billing invoice for this project by calculating all pending usage that hasn't been included in a previous invoice yet. If any fixed charges are configured in Billing Settings, they'll be applied to this invoice as well.
              </p>
              <p style={{ margin: "10px 0 0", fontSize: 13, fontWeight: 600, color: "#0f172a" }}>Are you sure you want to generate the invoice now?</p>

              <div style={{ marginTop: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>Due Date (optional)</label>
                <input
                  type="date"
                  style={{ width: "100%", border: "1px solid #e2e8f0", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none" }}
                  value={generateInvoiceDueDate}
                  onChange={(e) => setGenerateInvoiceDueDate(e.target.value)}
                />
                <p style={{ margin: "6px 0 0", fontSize: 11, color: "#94a3b8" }}>Leave blank to auto-calculate from the project's billing terms (issue date + payment due days).</p>
              </div>

              {generateInvoiceError && <p style={{ fontSize: 12, color: "#dc2626", margin: "14px 0 0" }}>{generateInvoiceError}</p>}

              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
                <button type="button" className="btn btn-light" style={{ fontSize: 13, borderRadius: 8 }} onClick={() => setShowGenerateInvoiceModal(false)} disabled={generatingInvoice}>Cancel</button>
                <button type="button" className="btn btn-primary d-flex align-items-center gap-2" style={{ fontSize: 13, borderRadius: 8 }} onClick={handleGenerateInvoice} disabled={generatingInvoice}>
                  <Zap size={14} /> {generatingInvoice ? "Generating…" : "Generate Invoice"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Create API Key Modal */}
      {showCreateKeyModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(15,23,42,0.4)", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowCreateKeyModal(false)}>
          <div style={{ background: "#fff", borderRadius: 14, width: 420, maxWidth: "92vw", boxShadow: "0 8px 32px rgba(15,23,42,0.18)" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid var(--borderColor)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: "#fff7ed", color: "var(--orangeColor)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Key size={15} />
                </div>
                <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Create API Key</h6>
              </div>
              <button style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b", padding: 4 }} onClick={() => setShowCreateKeyModal(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleCreateKey} style={{ padding: "20px", display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>Key Name <span style={{ color: "#dc2626" }}>*</span></label>
                <input
                  type="text"
                  style={{ width: "100%", border: "1px solid #e2e8f0", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none" }}
                  placeholder="e.g. Crawl Key"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  required
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>Expires At <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 400 }}>(optional)</span></label>
                <input
                  type="date"
                  style={{ width: "100%", border: "1px solid #e2e8f0", borderRadius: 8, padding: "9px 12px", fontSize: 13, outline: "none" }}
                  value={newKeyExpiry}
                  onChange={(e) => setNewKeyExpiry(e.target.value)}
                />
              </div>
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
                <button type="button" className="btn btn-light" style={{ fontSize: 13, borderRadius: 8 }} onClick={() => setShowCreateKeyModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary d-flex align-items-center gap-2" style={{ fontSize: 13, borderRadius: 8 }} disabled={newKeyCreating}>
                  <Key size={13} /> {newKeyCreating ? "Creating…" : "Create Key"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Raw Key Reveal Modal — shown once after creation */}
      {revealedRawKey && (
        <div style={{ position: "fixed", inset: 0, zIndex: 2100, background: "rgba(15,23,42,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 14, width: 460, maxWidth: "92vw", boxShadow: "0 8px 32px rgba(15,23,42,0.2)" }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--borderColor)", display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "#fff7ed", color: "var(--orangeColor)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Key size={15} />
              </div>
              <div>
                <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Save your API Key</h6>
                <p style={{ margin: 0, fontSize: 11, color: "#dc2626", fontWeight: 600 }}>This key is shown only once — copy it now.</p>
              </div>
            </div>
            <div style={{ padding: "20px" }}>
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 14px", marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
                <code style={{ flex: 1, fontSize: 12, color: "#334155", fontFamily: "monospace", wordBreak: "break-all", userSelect: "all" }}>
                  {revealedRawKey}
                </code>
                <button
                  className={`btn btn-sm d-flex align-items-center gap-1 ${copiedKeyPid === "__raw__" ? "btn-success" : "btn-outline-primary"}`}
                  style={{ fontSize: 12, borderRadius: 7, flexShrink: 0 }}
                  onClick={() => handleCopyKey(revealedRawKey, "__raw__")}
                >
                  {copiedKeyPid === "__raw__" ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                </button>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button className="btn btn-primary" style={{ fontSize: 13, borderRadius: 8 }} onClick={() => setRevealedRawKey(null)}>
                  I've saved it, close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <CreateEntityModal
        show={showCreateModal}
        type="manager"
        onClose={() => { setShowCreateModal(false); setManagerError(""); }}
        onConfirm={handleAssignManager}
        error={managerError}
        loading={managerLoading}
        projectPid={projectPid}
      />
      <DeleteConfirmModal
        show={showDeleteModal}
        type="manager"
        value={selectedManager?.email || ""}
        onClose={() => { setShowDeleteModal(false); setSelectedManager(null); }}
        onConfirm={handleRemoveManager}
      />

      {/* Remove Phone Mapping confirmation */}
      {removeMappingTarget && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => !removingMappingPid && setRemoveMappingTarget(null)}
        >
          <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 420, boxShadow: "0 8px 32px rgba(15,23,42,0.18)" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid var(--borderColor)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: "#fff1f2", color: "#dc2626", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <AlertTriangle size={17} />
                </div>
                <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Remove Phone Number</h6>
              </div>
              <button style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b", padding: 4 }} onClick={() => setRemoveMappingTarget(null)} disabled={!!removingMappingPid}>
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: "20px" }}>
              <p style={{ margin: "0 0 6px", fontSize: 14, color: "#334155" }}>
                Remove <strong>{removeMappingTarget.phoneNumber}</strong> from this project?
              </p>
              <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>
                Calls to this number will stop routing to this project until it's mapped again.
              </p>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", padding: "0 20px 20px" }}>
              <button className="btn btn-light" style={{ fontSize: 13, borderRadius: 8 }} onClick={() => setRemoveMappingTarget(null)} disabled={!!removingMappingPid}>
                Cancel
              </button>
              <button className="btn btn-danger d-flex align-items-center gap-2" style={{ fontSize: 13, borderRadius: 8, fontWeight: 600 }} onClick={confirmRemoveMapping} disabled={!!removingMappingPid}>
                <Trash2 size={14} /> {removingMappingPid ? "Removing…" : "Yes, Remove"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Domain confirmation — extra warning if it's the last one */}
      {removeDomainTarget && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => !removingDomainId && setRemoveDomainTarget(null)}
        >
          <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 420, boxShadow: "0 8px 32px rgba(15,23,42,0.18)" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid var(--borderColor)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: "#fff1f2", color: "#dc2626", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <AlertTriangle size={17} />
                </div>
                <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Remove Domain</h6>
              </div>
              <button style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b", padding: 4 }} onClick={() => setRemoveDomainTarget(null)} disabled={!!removingDomainId}>
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: "20px" }}>
              <p style={{ margin: "0 0 6px", fontSize: 14, color: "#334155" }}>
                Remove <strong>{removeDomainTarget.domain}</strong> from the whitelist?
              </p>
              {publicCredential?.domains?.length === 1 ? (
                <p style={{ margin: 0, fontSize: 12, color: "#dc2626", fontWeight: 600 }}>
                  This is the last allowed domain — removing it will disable the widget on your site until you add a new one.
                </p>
              ) : (
                <p style={{ margin: 0, fontSize: 12, color: "#94a3b8" }}>
                  The widget will stop working on this domain immediately.
                </p>
              )}
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", padding: "0 20px 20px" }}>
              <button className="btn btn-light" style={{ fontSize: 13, borderRadius: 8 }} onClick={() => setRemoveDomainTarget(null)} disabled={!!removingDomainId}>
                Cancel
              </button>
              <button className="btn btn-danger d-flex align-items-center gap-2" style={{ fontSize: 13, borderRadius: 8, fontWeight: 600 }} onClick={confirmRemoveDomain} disabled={!!removingDomainId}>
                <Trash2 size={14} /> {removingDomainId ? "Removing…" : "Yes, Remove"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Regenerate Secret confirmation — super_admin only */}
      {showRegenerateConfirm && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => !regenerating && setShowRegenerateConfirm(false)}
        >
          <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 440, boxShadow: "0 8px 32px rgba(15,23,42,0.18)" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid var(--borderColor)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: "#fff1f2", color: "#dc2626", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <AlertTriangle size={17} />
                </div>
                <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Regenerate Widget Secret</h6>
              </div>
              <button style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b", padding: 4 }} onClick={() => setShowRegenerateConfirm(false)} disabled={regenerating}>
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: "20px" }}>
              <p style={{ margin: 0, fontSize: 13, color: "#334155", lineHeight: 1.6 }}>
                This will immediately break any site currently using the old widget secret until you update it with the new one. Continue?
              </p>
              {regenerateError && <p style={{ fontSize: 12, color: "#dc2626", margin: "14px 0 0" }}>{regenerateError}</p>}
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", padding: "0 20px 20px" }}>
              <button className="btn btn-light" style={{ fontSize: 13, borderRadius: 8 }} onClick={() => setShowRegenerateConfirm(false)} disabled={regenerating}>
                Cancel
              </button>
              <button className="btn btn-danger d-flex align-items-center gap-2" style={{ fontSize: 13, borderRadius: 8, fontWeight: 600 }} onClick={handleRegenerateSecret} disabled={regenerating}>
                <RefreshCw size={14} /> {regenerating ? "Regenerating…" : "Yes, Regenerate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reveal new secret — shown once immediately after regeneration */}
      {revealedWidgetSecret && (
        <div style={{ position: "fixed", inset: 0, zIndex: 2100, background: "rgba(15,23,42,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 14, width: 460, maxWidth: "92vw", boxShadow: "0 8px 32px rgba(15,23,42,0.2)" }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--borderColor)", display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "var(--primaryTint)", color: "var(--primaryColor)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ShieldCheck size={15} />
              </div>
              <div>
                <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#0f172a" }}>Save your new widget secret</h6>
                <p style={{ margin: 0, fontSize: 11, color: "#dc2626", fontWeight: 600 }}>This secret is shown only once — copy it now.</p>
              </div>
            </div>
            <div style={{ padding: "20px" }}>
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 14px", marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
                <code style={{ flex: 1, fontSize: 12, color: "#334155", fontFamily: "monospace", wordBreak: "break-all", userSelect: "all" }}>
                  {revealedWidgetSecret}
                </code>
                <button
                  className={`btn btn-sm d-flex align-items-center gap-1 ${copiedWidgetSecret ? "btn-success" : "btn-outline-primary"}`}
                  style={{ fontSize: 12, borderRadius: 7, flexShrink: 0 }}
                  onClick={handleCopyWidgetSecret}
                >
                  {copiedWidgetSecret ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                </button>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button className="btn btn-primary" style={{ fontSize: 13, borderRadius: 8 }} onClick={() => setRevealedWidgetSecret(null)}>
                  I've saved it, close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default ProjectDetails;
